/**
 * The meeting-notes list for one Granola account, on its View page.
 *
 * This used to be a collapsed accordion inside the /sources account card. It
 * was collapsed for a good reason — a personal key's corpus runs to hundreds of
 * notes (measured: 938), so it must never render wholesale — but a page of its
 * own gets that for free: the list is the reason you navigated here, so it
 * loads open, and pagination ("Load more", 30 at a time) does the bounding that
 * the accordion was doing.
 *
 * Each row links to /sources/collection/:id, the same per-collection View that
 * WhatsApp/Signal chat rows use — so a meeting drills down to its own
 * transcript rather than dead-ending at a title.
 */
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import { StatusPill } from '@/components/patterns/status-pill';
import { formatRelative } from '@/components/sources/message-log';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { connectorApi, type GranolaMeeting, granolaMeetingsApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

/** Per-meeting ingestion toggle — same control as the Signal/WAHA ChatRow. */
function IngestSwitch({
  on,
  disabled,
  onClick,
}: {
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={on ? 'Disable ingestion' : 'Enable ingestion'}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative h-6 w-[42px] shrink-0 rounded-full transition-colors disabled:opacity-50',
        on ? 'bg-primary' : 'bg-[#e2e2e5]',
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 left-[3px] h-[18px] w-[18px] -translate-y-1/2 rounded-full bg-white transition-transform',
          on && 'translate-x-[18px]',
        )}
      />
    </button>
  );
}

function MeetingRow({ accountId, meeting }: { accountId: string; meeting: GranolaMeeting }) {
  const qc = useQueryClient();
  const ingMut = useMutation({
    mutationFn: (enabled: boolean) => connectorApi.patchCollection(meeting.id, enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.granolaMeetings(accountId) }),
    onError: () => toast.error('Failed to update ingestion'),
  });

  return (
    <div className="flex items-center gap-3 border-border-subtle border-b px-4 py-3 last:border-b-0">
      <span className="min-w-0 flex-1 truncate text-[14px]">{meeting.title}</span>
      <span className="shrink-0 text-[12px] text-faint">
        {formatRelative(meeting.lastActivityAt)}
      </span>
      <StatusPill tone={meeting.ingestionEnabled ? 'active' : 'paused'}>
        {meeting.ingestionEnabled ? 'Indexing' : 'Paused'}
      </StatusPill>
      <Link
        to="/sources/collection/$collectionId"
        params={{ collectionId: meeting.id }}
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
      >
        View
      </Link>
      <IngestSwitch
        on={meeting.ingestionEnabled}
        disabled={ingMut.isPending}
        onClick={() => ingMut.mutate(!meeting.ingestionEnabled)}
      />
    </div>
  );
}

export function GranolaMeetingsPanel({ accountId }: { accountId: string }) {
  const query = useInfiniteQuery({
    queryKey: qk.granolaMeetings(accountId),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      granolaMeetingsApi.list(accountId, { limit: 30, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const meetings = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mt-6">
      <h2 className="flex items-center gap-2 font-semibold text-base">
        <NotebookPen className="h-4 w-4 text-faint" />
        Meeting notes
      </h2>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Every meeting this key can see. Turn one off to remove its notes and transcript from search.
      </p>

      <div className="mt-3">
        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-11 w-full rounded-lg" />
            <Skeleton className="h-11 w-full rounded-lg" />
            <Skeleton className="h-11 w-full rounded-lg" />
          </div>
        ) : query.isError ? (
          <p className="text-[13px] text-destructive">Failed to load meeting notes.</p>
        ) : meetings.length === 0 ? (
          <p className="rounded-lg border border-border-subtle p-4 text-[13px] text-faint">
            No meeting notes indexed yet.
          </p>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border-subtle">
              {meetings.map((m) => (
                <MeetingRow key={m.id} accountId={accountId} meeting={m} />
              ))}
            </div>
            {query.hasNextPage && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'sm' }),
                    'min-w-[100px]',
                  )}
                >
                  {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
